import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayPrice,
  isPriceVariationsEnabled,
  requireListingImage,
} from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing } from '../../util/data';
import { getFirstListingImage, getListingImageVariantNames } from '../../util/listingImages';
import {
  formatListingAge,
  formatListingDate,
  getListingAgeSourceDate,
  normalizeManagedStockQuantity,
} from '../../util/listingRules';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { useFavoriteListings } from '../../hooks/useFavoriteListings';
import { isAuthenticatedSelector } from '../../ducks/auth.duck';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
  PrimaryButton,
  PriceDisplay,
} from '../../components';

import css from './ListingCard.module.css';
import Icons from '../Icons/Icons';
import { formatUnixToDateTime, getCountdownParts, pad2 } from '../../util/genericHelpers';
import { getTimedSaleEndUnix, isActiveTimedSaleListing } from '../../util/timedSales';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const titleCaseFromSlug = str =>
  typeof str === 'string' ? str.replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';

const formatPublicMoney = (moneyLike, intl) => {
  // publicData.buyNowPrice is plain object in your example: { amount: 3400, currency: "USD" }
  // Amounts are stored in cents; convert to dollars for display.
  if (!moneyLike?.currency || typeof moneyLike?.amount !== 'number') return '';
  return intl.formatNumber(moneyLike.amount / 100, {
    style: 'currency',
    currency: moneyLike.currency,
    maximumFractionDigits: 0,
  });
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const getHighestBidMoney = (userBids, fallbackCurrency) => {
  if (!Array.isArray(userBids) || userBids.length === 0) return null;
  let highest = null;
  userBids.forEach(bid => {
    const amount = Number(bid?.biddingAmount);
    if (!Number.isFinite(amount)) return;
    if (!highest || amount > highest.amount) {
      highest = {
        amount,
        currency: bid?.biddingCurrency || fallbackCurrency,
      };
    }
  });
  return highest;
};

const PriceMaybe = props => {
  const {
    price,
    publicData,
    config,
    intl,
    listingTypeConfig,
    isAuction,
    reservePrice,
    highestBidMoney,
    bidCount,
  } = props;
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const showPrice = displayPrice(listingTypeConfig);
  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;
  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);

  if (isAuction) {
    const hasBids = Number(bidCount || 0) > 0;
    const moneyLike = hasBids ? highestBidMoney || reservePrice : reservePrice;
    const formattedAuctionPrice = formatPublicMoney(moneyLike, intl);
    if (!formattedAuctionPrice) return null;

    const priceValue = (
      <h4 className={css.priceValue}>
        <PriceDisplay value={formattedAuctionPrice} />
      </h4>
    );

    const titleValue = isAuthenticated ? formattedAuctionPrice : undefined;
    const labelId =
      hasBids && highestBidMoney ? 'ListingCard.highestBidLabel' : 'ListingCard.reservePriceLabel';

    return (
      <div className={css.price} title={titleValue}>
        <span className={css.buyNowPrice}>
          <FormattedMessage id={labelId} />
        </span>
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit: '' }} />
      </div>
    );
  }

  // ✅ Prefer listing.price (Money) if exists, else fallback to publicData.buyNowPrice (plain object)
  const { formattedPrice: formattedListingPrice, priceTitle } = priceData(
    price,
    config.currency,
    intl
  );
  const formattedBuyNow = formatPublicMoney(publicData?.buyNowPrice, intl);

  const formattedPriceToShow = formattedListingPrice || formattedBuyNow || '';

  const priceValue = (
    <h4 className={css.priceValue}>
      <PriceDisplay value={formattedPriceToShow} />
    </h4>
  );

  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      {/* <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} /> */}
    </span>
  ) : (
    ''
  );

  if (!formattedPriceToShow) return null;

  const titleValue = isAuthenticated ? priceTitle || formattedPriceToShow : undefined;

  return (
    <div className={css.price} title={titleValue}>
      <span className={css.buyNowPrice}>
        <FormattedMessage id="ListingCard.buyNowPriceLabel" />
      </span>

      {hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : (
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
      )}
    </div>
  );
};

const ListingCardImage = props => {
  const [nowTick, setNowTick] = useState(0);
  const intl = useIntl();
  const {
    currentListing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    showListingImage,
    style,
    isFavorited,
    onToggleFavorite,
    favoriteLabel,
  } = props;

  const firstImage = getFirstListingImage(currentListing);
  const variants = getListingImageVariantNames(firstImage, variantPrefix);
  const publicData = currentListing?.attributes?.publicData || {};

  const auctionEndUnix = getTimedSaleEndUnix(publicData);
  const auctionEnabled = isActiveTimedSaleListing(publicData);
  const auctionEndMs = auctionEndUnix ? auctionEndUnix * 1000 : 0;
  const countdown = useMemo(() => getCountdownParts(auctionEndMs), [auctionEndMs, nowTick]);
  const endsLabel = auctionEndUnix ? formatUnixToDateTime(auctionEndUnix, intl) : '';

  useEffect(() => {
    if (!auctionEnabled) return;
    const id = setInterval(() => setNowTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [auctionEnabled]);

  const handleToggleFavorite = e => {
    e.preventDefault();
    e.stopPropagation();
    if (onToggleFavorite) {
      onToggleFavorite();
    }
  };

  const favoriteButton = onToggleFavorite ? (
    <button
      type="button"
      className={classNames(css.favoriteButton, isFavorited ? css.favoriteButtonActive : null)}
      aria-pressed={isFavorited}
      aria-label={favoriteLabel}
      onClick={handleToggleFavorite}
    >
      <Icons name={isFavorited ? 'favorite' : 'unfavorite'} />
    </button>
  ) : null;

  const countdownBadge = auctionEnabled ? (
    <span className={css.countdownBadge} title={endsLabel}>
      {pad2(countdown.days)} : {pad2(countdown.hrs)} : {pad2(countdown.mins)} :{' '}
      {pad2(countdown.secs)}
    </span>
  ) : null;

  return showListingImage ? (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <LazyImage
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />

      <div className={css.topInfo}>
        {countdownBadge}
        {favoriteButton}
      </div>
    </AspectRatioWrapper>
  ) : (
    <div className={css.thumbnailWrapper}>
      <ListingCardThumbnail
        style={style}
        listingTitle={title}
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        setActivePropsMaybe={setActivePropsMaybe}
      />
      <div className={css.topInfo}>
        {countdownBadge}
        {favoriteButton}
      </div>
    </div>
  );
};

export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const { className, rootClassName, listing, renderSizes, setActiveListing } = props;

  const classes = classNames(rootClassName || css.root, className);

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { isFavorited, toggleFavorite } = useFavoriteListings();
  const isListingFavorited = isFavorited(id);
  const favoriteLabel = isListingFavorited
    ? intl.formatMessage({ id: 'Favorites.toggleRemove' })
    : intl.formatMessage({ id: 'Favorites.toggleAdd' });

  const { title = '', price, publicData = {} } = currentListing.attributes;
  const metadata = currentListing?.attributes?.metadata || {};
  const slug = createSlug(title);

  // Dynamic fields from publicData (based on your example)
  const distillery = titleCaseFromSlug(publicData?.producer);
  const filled = formatListingDate(getListingAgeSourceDate(publicData), intl);
  const listingAgeLabel = formatListingAge(publicData, intl);
  const mashbill = publicData?.mashbill ?? '';
  const quantity = normalizeManagedStockQuantity(currentListing.currentStock?.attributes?.quantity);
  const isAuction = publicData?.isAuction === true;
  const userBids = Array.isArray(metadata?.userBids) ? metadata.userBids : [];
  const bidCount = userBids.length;
  const highestBidMoney = getHighestBidMoney(userBids, config.currency);
  const reservePrice = publicData?.reservePrice || null;

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;
  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <ListingCardImage
        renderSizes={renderSizes}
        title={title}
        currentListing={currentListing}
        config={config}
        setActivePropsMaybe={setActivePropsMaybe}
        aspectWidth={aspectWidth}
        aspectHeight={aspectHeight}
        variantPrefix={variantPrefix}
        style={cardStyle}
        showListingImage={showListingImage}
        isFavorited={isListingFavorited}
        onToggleFavorite={() => toggleFavorite(id)}
        favoriteLabel={favoriteLabel}
      />

      <div className={css.info}>
        {showListingImage && (
          <h4 className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </h4>
        )}

        <div className={css.mainInfo}>
          <div className={css.infoDetails}>
            <span>
              <FormattedMessage id="ListingCard.distilleryLabel" />
            </span>
            <p>{distillery || '-'}</p>
          </div>

          <div className={css.infoDetails}>
            <span>
              <FormattedMessage id="ListingCard.ageLabel" />
            </span>
            <p>{listingAgeLabel || '-'}</p>
          </div>

          <div className={css.infoDetails}>
            <span>
              <FormattedMessage id="ListingCard.mashbillLabel" />
            </span>
            <p>{mashbill || '-'}</p>
          </div>

          <div className={css.infoDetails}>
            <span>
              <FormattedMessage id="ListingCard.quantityLabel" />
            </span>
            <p>{quantity != null ? quantity : '-'}</p>
          </div>
        </div>

        <div className={css.priceSec}>
          <PriceMaybe
            price={price}
            publicData={publicData}
            config={config}
            intl={intl}
            listingTypeConfig={foundListingTypeConfig}
            isAuction={isAuction}
            reservePrice={reservePrice}
            highestBidMoney={highestBidMoney}
            bidCount={bidCount}
          />

          <PrimaryButton>
            <FormattedMessage
              id={isAuction ? 'ListingCard.placeBidButton' : 'ListingCard.purchaseButton'}
            />
          </PrimaryButton>
        </div>
      </div>
    </NamedLink>
  );
};

export default ListingCard;
